import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { TrainingBanner } from "@/components/TrainingBanner";
import { TrainingScenarios } from "@/components/TrainingScenarios";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Register from "@/pages/Register";
import NewRequest from "@/pages/NewRequest";
import ReworkDetail from "@/pages/ReworkDetail";
import Settings from "@/pages/Settings";
import Board from "@/pages/Board";
import Rack from "@/pages/Rack";
import Maintenance from "@/pages/Maintenance";
import Scan from "@/pages/Scan";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f7f5f1] text-[#6b6a68] font-mono text-sm" data-testid="auth-loading">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleGate({ allow, deny, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const home = user.role === "kiosk" ? "/board" : user.role === "maintenance" ? "/maintenance" : "/";
  if (allow && !allow.includes(user.role)) return <Navigate to={home} replace />;
  if (deny && deny.includes(user.role)) return <Navigate to={home} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user?.role === "kiosk") return <Navigate to="/board" replace />;
  if (user?.role === "maintenance") return <Navigate to="/maintenance" replace />;
  return <Dashboard />;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={user.role === "kiosk" ? "/board" : "/"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TrainingBanner />
          <Toaster richColors position="top-right" theme="light" toastOptions={{ style: { fontFamily: "Figtree", borderRadius: 2 } }} />
          <Routes>
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Home />} />
              <Route path="/board" element={<RoleGate deny={["maintenance"]}><Board /></RoleGate>} />
              <Route path="/rack" element={<RoleGate deny={["maintenance"]}><Rack /></RoleGate>} />
              <Route path="/maintenance" element={<RoleGate><Maintenance /></RoleGate>} />
              <Route path="/scan" element={<RoleGate deny={["maintenance", "management"]}><Scan /></RoleGate>} />
              <Route path="/register" element={<RoleGate deny={["kiosk", "maintenance"]}><Register /></RoleGate>} />
              <Route path="/new" element={<RoleGate deny={["kiosk", "management", "maintenance"]}><NewRequest /></RoleGate>} />
              <Route path="/rework/:id" element={<ReworkDetail />} />
              <Route path="/settings" element={<RoleGate allow={["admin"]}><Settings /></RoleGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <TrainingScenarios />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
